// src/components/dashboard/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserPCRForms } from '../../services/pcrService';
import Navbar from '../layout/Navbar';
import { getStudentByUID } from '../../utils/studentMapUtils';

interface FormListItem {
  id: string;
  userId: string;
  timestamp: any;
  lastSaved: any;
}

const Dashboard: React.FC = () => {
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (currentUser && (currentUser.email === 'jhatcher4@ccc.edu' || currentUser.email === 'admin@example.com')) {
      setIsAdmin(true);
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchForms = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const result = await getUserPCRForms(currentUser.uid);
        
        if (result.success) {
          setForms(result.forms || []);
        } else {
          setError('Failed to load forms');
        }
      } catch (err) {
        setError('An error occurred while fetching forms');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchForms();
  }, [currentUser]);

  const handleCreateNew = () => {
    navigate('/form/new');
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Helper function to get student name for a form
  const getStudentName = (form: FormListItem): string => {
    if (form.userId) {
      const student = getStudentByUID(form.userId);
      if (student && student.name) {
        return student.name;
      }
    }
    return '';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Your PCR Forms</h1>
            {/* Only show create button for admins */}
            {isAdmin && (
              <button
                onClick={handleCreateNew}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Create New Form
              </button>
            )}
          </div>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
          ) : forms.length === 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6 text-center">
                <p className="text-gray-700">You don't have any PCR forms yet.</p>
                {isAdmin && (
                  <button
                    onClick={handleCreateNew}
                    className="mt-4 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Create your first PCR form
                  </button>
                )}
                {!isAdmin && (
                  <p className="mt-4 text-sm text-gray-500">
                    Please contact your instructor if you don't see your assigned form.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <ul className="divide-y divide-gray-200">
                {forms.map((form) => {
                  const studentName = getStudentName(form);
                  return (
                    <li key={form.id}>
                      <div className="px-4 py-4 sm:px-6">
                        <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-medium text-indigo-600 truncate">
                              PCR Form{studentName ? ` - ${studentName}` : ''}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                              Created: {formatDate(form.timestamp)}
                            </p>
                          </div>
                          <div className="mt-4 flex-shrink-0 sm:mt-0">
                            <p className="text-sm text-gray-500">
                              Last saved: {formatDate(form.lastSaved)}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 flex justify-start space-x-3">
                          <Link 
                            to={`/form/${form.id}`}
                            className="px-3 py-1 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-100"
                          >
                            Edit Form
                          </Link>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              const url = `${window.location.origin}/form/${form.id}/readonly`;
                              navigator.clipboard.writeText(url)
                                .then(() => alert('Read-only link copied to clipboard!'))
                                .catch(err => console.error('Failed to copy link: ', err));
                            }}
                            className="px-3 py-1 text-sm text-green-600 border border-green-600 rounded-md hover:bg-green-100"
                          >
                            Copy Read-Only Link
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;